import { BlendFunction, Effect } from "postprocessing";
import * as THREE from "three";

/**
 * Photographic film grain as a merged `postprocessing` effect.
 *
 * Why not the library's `Noise` effect: it reseeds from the frame time on
 * every render, so two consecutive captures of an unchanged scene differ
 * and the visual baselines (which wait for two identical frames) can never
 * settle. This grain is a pure function of the pixel coordinate, so a still
 * scene stays still. It rides the camera like dust on a lens, which at this
 * strength reads as a paper-like tooth rather than a dirty screen.
 *
 * The grain is luminance-weighted the way film is: strongest in the
 * midtones, faint in the blacks (so the dusk ground doesn't fizz) and in
 * the highlights (so the lit glass stays clean). Two octaves of hashed
 * noise at 1 px and ~2 px break the regular single-pixel pattern.
 */
const FRAGMENT_SHADER = /* glsl */ `
uniform float uStrength;

float grainHash(vec2 p) {
	vec3 p3 = fract(vec3(p.xyx) * 0.1031);
	p3 += dot(p3, p3.yzx + 33.33);
	return fract((p3.x + p3.y) * p3.z);
}

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
	vec2 px = floor(uv * resolution);
	float fine = grainHash(px) - 0.5;
	float coarse = grainHash(floor(px * 0.5) + 71.0) - 0.5;
	float grain = fine * 0.65 + coarse * 0.35;
	float luma = dot(inputColor.rgb, vec3(0.2126, 0.7152, 0.0722));
	// Bell over luminance: 0 at black and white, 1 at mid-grey.
	float weight = 4.0 * luma * (1.0 - luma);
	outputColor = vec4(inputColor.rgb + grain * uStrength * weight, inputColor.a);
}
`;

export interface FilmGrainEffectOptions {
	/** Peak grain amplitude at mid-grey, in output colour units. */
	strength?: number;
}

export class FilmGrainEffect extends Effect {
	constructor({ strength = 0.035 }: FilmGrainEffectOptions = {}) {
		super("FilmGrainEffect", FRAGMENT_SHADER, {
			blendFunction: BlendFunction.NORMAL,
			uniforms: new Map([["uStrength", new THREE.Uniform(strength)]]),
		});
	}
}
